import { lazy, Suspense, useMemo, useState, type ComponentProps } from "react";
import { BarChart3, Database, Table2 } from "lucide-react";
import { formatDate, formatNumber, useLocale, useTranslation, type Locale } from "@ncp/i18n";
import { cn } from "@ncp/ui";
import type { EChart as EChartComponent } from "@ncp/ui/components/charts";

type ChartOption = ComponentProps<typeof EChartComponent>["option"];

// ECharts (≈ 1 MB) loads when a turn first shows a chart, not with the conversation (shell-ia §18 budget; WP-SHELL request).
const EChart = lazy(() => import("@ncp/ui/components/charts").then((m) => ({ default: m.EChart })));
/** The platform chart palette (`@ncp/ui` charts), kept here so drawing an option does not load the chart library. */
const chartPalette = ["#92722a", "#3788a0", "#c08153", "#dabc70", "#2e5b6f", "#94876e", "#b36e45", "#52a4ba"];

/** A table artifact (`kind: Table`) and the `table` of a proposal's `sourceData`: labelled columns, rows of cells, provenance. */
export interface DataTableSpec {
  title?: string | null;
  columns: string[];
  rows: unknown[][];
  notes?: string | null;
  source?: string | null;
  retrievedAt?: string | null;
}

export type DataChartType = "line" | "column" | "bar" | "pie";

/** A chart artifact (`kind: Chart`): category labels with one or more numeric series. */
export interface DataChartSpec {
  type?: DataChartType | null;
  title?: string | null;
  labels: string[];
  series: Array<{ name: string; data: Array<number | null> }>;
  unit?: string | null;
  source?: string | null;
}

/** Data an agent pulled from a connector for a proposal (`structured.sourceData[]`). */
export interface SourceDataEntry {
  key: string;
  source?: string | null;
  title?: string | null;
  table?: DataTableSpec | null;
  chart?: DataChartSpec | null;
  retrievedAt?: string | null;
}

const asObject = (content: unknown): Record<string, unknown> | null => {
  if (content && typeof content === "object" && !Array.isArray(content)) return content as Record<string, unknown>;
  if (typeof content !== "string") return null;
  try { const v = JSON.parse(content) as unknown; return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null; } catch { return null; }
};
const text = (v: unknown) => (typeof v === "string" && v.trim() ? v : null);

export function parseTableSpec(content: unknown): DataTableSpec | null {
  const v = asObject(content);
  if (!v || !Array.isArray(v.columns) || !Array.isArray(v.rows)) return null;
  return {
    title: text(v.title), columns: v.columns.map(String), rows: v.rows.filter(Array.isArray) as unknown[][],
    notes: text(v.notes), source: text(v.source), retrievedAt: text(v.retrievedAt),
  };
}

export function parseChartSpec(content: unknown): DataChartSpec | null {
  const v = asObject(content);
  if (!v || !Array.isArray(v.labels) || !Array.isArray(v.series)) return null;
  const series = (v.series as unknown[]).map((s) => asObject(s)).filter((s): s is Record<string, unknown> => !!s && Array.isArray(s.data))
    .map((s) => ({ name: String(s.name ?? ""), data: (s.data as unknown[]).map((d) => (typeof d === "number" && Number.isFinite(d) ? d : typeof d === "string" && d.trim() !== "" && !Number.isNaN(Number(d)) ? Number(d) : null)) }));
  if (series.length === 0) return null;
  const type = ["line", "column", "bar", "pie"].includes(String(v.type)) ? (v.type as DataChartType) : "column";
  return { type, title: text(v.title), labels: (v.labels as unknown[]).map(String), series, unit: text(v.unit), source: text(v.source) };
}

/** `structured.sourceData` of a proposal, when an agent drew on connected data sources. */
export function sourceDataOf(structured: unknown): SourceDataEntry[] {
  const s = asObject(structured);
  if (!s || !Array.isArray(s.sourceData)) return [];
  return (s.sourceData as unknown[]).map((e) => asObject(e)).filter((e): e is Record<string, unknown> => !!e)
    .map((e) => ({ key: String(e.key ?? ""), source: text(e.source), title: text(e.title), table: parseTableSpec(e.table), chart: parseChartSpec(e.chart), retrievedAt: text(e.retrievedAt) }))
    .filter((e) => e.table || e.chart);
}

function cellText(v: unknown, locale: Locale): string {
  if (v == null) return "";
  if (typeof v === "number") return Number.isInteger(v) && Math.abs(v) >= 1900 && Math.abs(v) <= 2100 ? String(v) : formatNumber(v, locale, { maximumFractionDigits: 2 });
  if (typeof v === "boolean") return v ? "✓" : "—";
  return typeof v === "object" ? JSON.stringify(v) : String(v);
}

function Provenance({ source, retrievedAt, notes }: { source?: string | null; retrievedAt?: string | null; notes?: string | null }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  if (!source && !retrievedAt && !notes) return null;
  return (
    <p className="mt-1.5 text-[11px] text-muted-foreground">
      {source ? <>{t("ai.data.source")}: <span className="font-medium text-aeblack-700">{source}</span></> : null}
      {notes ? <>{source ? " · " : ""}{notes}</> : null}
      {retrievedAt ? <>{source || notes ? " · " : ""}{t("ai.data.retrieved", { date: formatDate(retrievedAt, locale) })}</> : null}
    </p>
  );
}

/** A data table with numbers aligned and formatted, a sticky header and its provenance line. */
export function DataTableView({ table, className, maxHeight = "max-h-[28rem]", showTitle = false }: { table: DataTableSpec; className?: string; maxHeight?: string; showTitle?: boolean }) {
  const { locale } = useLocale();
  const numeric = useMemo(() => table.columns.map((_, j) => {
    const cells = table.rows.map((r) => r[j]).filter((c) => c != null && c !== "");
    return cells.length > 0 && cells.filter((c) => typeof c === "number").length / cells.length >= 0.8;
  }), [table]);
  return (
    <div className={className}>
      {showTitle && table.title ? <p className="mb-1.5 text-sm font-semibold">{table.title}</p> : null}
      <div className={cn("overflow-auto rounded-xl border border-sand-200", maxHeight)}>
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-sand-50 text-xs uppercase tracking-wider text-sand-700">
            <tr>{table.columns.map((c, j) => <th key={j} className={cn("px-3 py-2 font-semibold", numeric[j] ? "text-end" : "text-start")}>{c}</th>)}</tr>
          </thead>
          <tbody>
            {table.rows.map((r, i) => (
              <tr key={i} className="border-t border-sand-100">
                {table.columns.map((_, j) => <td key={j} className={cn("px-3 py-1.5", numeric[j] && "tabular text-end")}>{cellText(r[j], locale)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Provenance source={table.source} retrievedAt={table.retrievedAt} notes={table.notes} />
    </div>
  );
}

function chartOption(chart: DataChartSpec, locale: Locale): ChartOption {
  const fmt = (v: unknown) => (typeof v === "number" ? formatNumber(v, locale, { maximumFractionDigits: 2 }) : String(v ?? "—"));
  const unit = chart.unit ? ` ${chart.unit}` : "";
  const legend = chart.series.length > 1 ? { bottom: 0, type: "scroll" as const, textStyle: { fontSize: 11 } } : undefined;
  if (chart.type === "pie") {
    const first = chart.series[0]!;
    return {
      tooltip: { trigger: "item", valueFormatter: (v) => `${fmt(v)}${unit}` },
      legend: { bottom: 0, type: "scroll", textStyle: { fontSize: 11 } },
      series: [{ type: "pie", radius: ["38%", "68%"], center: ["50%", "45%"], data: chart.labels.map((name, i) => ({ name, value: first.data[i] ?? 0 })), label: { fontSize: 11 } }],
    };
  }
  const horizontal = chart.type === "bar";
  const category = { type: "category" as const, data: chart.labels, axisLabel: { fontSize: 11, hideOverlap: true }, axisLine: { lineStyle: { color: "#e3dccd" } } };
  // A line reads as a trend, so its axis fits the data; bars keep a zero baseline so their lengths stay comparable.
  const value = { type: "value" as const, scale: chart.type === "line", name: chart.unit ?? undefined, nameTextStyle: { fontSize: 11 }, axisLabel: { fontSize: 11, formatter: (v: number) => fmt(v) }, splitLine: { lineStyle: { color: "#f0ebe1" } } };
  return {
    tooltip: { trigger: "axis", valueFormatter: (v) => `${fmt(v)}${unit}` },
    legend,
    grid: { left: 12, right: 16, top: 24, bottom: legend ? 36 : 12, containLabel: true },
    xAxis: horizontal ? value : category,
    yAxis: horizontal ? { ...category, inverse: true } : value,
    series: chart.series.map((s, i) => ({
      name: s.name, data: s.data, type: chart.type === "line" ? ("line" as const) : ("bar" as const),
      ...(chart.type === "line" ? { symbol: "circle", symbolSize: 5, lineStyle: { width: 2 }, connectNulls: true } : { barMaxWidth: 28 }),
      itemStyle: { color: chartPalette[i % chartPalette.length] },
    })),
  };
}

/** The data behind a proposal: one entry per connector call, each as a table or a chart. */
export function SourceDataPanel({ entries, className }: { entries: SourceDataEntry[]; className?: string }) {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const [asChart, setAsChart] = useState(false);
  const entry = entries[Math.min(active, entries.length - 1)];
  if (!entry) return null;
  const showChart = !!entry.chart && (asChart || !entry.table);
  return (
    <div className={cn("rounded-xl border border-seablue-200 bg-seablue-50/30 p-3", className)}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="flex items-center gap-1.5 text-xs font-semibold text-seablue-900"><Database className="size-3.5" /> {t("ai.data.sourceData")}</p>
        {entry.table && entry.chart ? (
          <span className="ms-auto inline-flex rounded-lg border border-sand-200 bg-white p-0.5">
            <button type="button" onClick={() => setAsChart(false)} className={cn("flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px]", !showChart ? "bg-aegold-100 text-aeblack-900" : "text-muted-foreground")}><Table2 className="size-3" /> {t("ai.data.table")}</button>
            <button type="button" onClick={() => setAsChart(true)} className={cn("flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px]", showChart ? "bg-aegold-100 text-aeblack-900" : "text-muted-foreground")}><BarChart3 className="size-3" /> {t("ai.data.chart")}</button>
          </span>
        ) : null}
      </div>
      <p className="mt-0.5 text-[11px] text-muted-foreground">{t("ai.data.sourceDataHint")}</p>
      {entries.length > 1 ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {entries.map((e, i) => (
            <button key={`${e.key}-${i}`} type="button" onClick={() => setActive(i)}
              className={cn("max-w-full truncate rounded-lg border px-2 py-0.5 text-[11px]", i === active ? "border-aegold-400 bg-aegold-50 text-aeblack-900" : "border-sand-200 bg-white text-muted-foreground hover:border-aegold-300")}>
              {e.title ?? e.key}
            </button>
          ))}
        </div>
      ) : null}
      <div className="mt-2">
        {entries.length === 1 && entry.title ? <p className="mb-1.5 text-sm font-medium">{entry.title}</p> : null}
        {showChart ? <DataChartView chart={entry.chart!} /> : entry.table ? <DataTableView table={{ ...entry.table, source: entry.table.source ?? entry.source, retrievedAt: entry.table.retrievedAt ?? entry.retrievedAt }} maxHeight="max-h-72" /> : null}
      </div>
    </div>
  );
}

/** A chart artifact drawn with the platform's chart component, with its provenance line. */
export function DataChartView({ chart, className, height, showTitle = false }: { chart: DataChartSpec; className?: string; height?: number; showTitle?: boolean }) {
  const { locale } = useLocale();
  const option = useMemo(() => chartOption(chart, locale), [chart, locale]);
  const rows = chart.type === "bar" ? chart.labels.length : 0;
  return (
    <div className={className}>
      {showTitle && chart.title ? <p className="mb-1.5 text-sm font-semibold">{chart.title}</p> : null}
      <div className="rounded-xl border border-sand-200 bg-white p-2">
        <Suspense fallback={<div className="animate-pulse rounded-lg bg-sand-100/70" style={{ height: height ?? Math.max(260, Math.min(560, rows * 26 + 80)) }} />}>
          <EChart option={option} height={height ?? Math.max(260, Math.min(560, rows * 26 + 80))} rtl={locale === "ar"} />
        </Suspense>
      </div>
      <Provenance source={chart.source} />
    </div>
  );
}
